import { Bar } from 'react-chartjs-2'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js'
import type { Submission } from '../../lib/types'
import { count, pct } from '../../lib/stats'
import { C, hmColor } from '../../lib/colors'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

const BASE = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }
const GRID_COLOR = 'rgba(1,87,98,0.07)'

const DIAGS = ['none', 'pcos', 'insulin-resistance', 'thyroid', 'endometriosis', 'unsure']
const DLABS = ['None', 'PCOS', 'Insulin Resistance', 'Thyroid', 'Endometriosis', 'Unsure']
const AXES  = ['prolactin', 'androgens', 'insulin', 'thyroid'] as const
const ALABS = ['Prolactin', 'Androgens', 'Insulin', 'Thyroid']

interface Props { data: Submission[] }

export function Diagnoses({ data }: Props) {
  const N = data.length

  return (
    <section className="sec" id="diagnoses">
      <div className="sec-hd">
        <div className="sec-label">06 · Diagnosis Profile</div>
        <h2 className="sec-title">Pre-existing conditions and their signal correlates</h2>
        <p className="sec-sub">previous_diagnoses field. Women may carry multiple diagnoses. Co-occurrence with hormone signals validates methodology.</p>
      </div>
      <div className="g12">
        <div className="card">
          <div className="card-title">Diagnosis prevalence</div>
          <div className="card-sub">% of respondents with each condition</div>
          <div className="cw h260">
            <Bar
              data={{ labels: DLABS, datasets: [{ data: DIAGS.map(dx => pct(count(data, d => d.previous_diagnoses.includes(dx)), N)), backgroundColor: [C.t3, C.rose, C.amber, C.teal2, C.violet, C.a(C.t3,.5)], borderRadius: 4 }] }}
              options={{ ...BASE, indexAxis: 'y' as const, scales: { x: { max: 100, grid: { color: GRID_COLOR } }, y: { grid: { display: false } } } }}
            />
          </div>
        </div>
        <div className="card">
          <div className="card-title">Diagnosis × Hormone signal matrix</div>
          <div className="card-sub">Among respondents with each diagnosis: % with each axis signalled</div>
          <div className="hm-wrap" style={{ marginTop: 6 }}>
            <div className="hm-grid hm-diag-grid">
              <div className="hm-hdr" />
              {ALABS.map(l => <div key={l} className="hm-hdr">{l}</div>)}
              {DIAGS.map((dx, di) => {
                const grp = data.filter(d => d.previous_diagnoses.includes(dx))
                return (
                  <>
                    <div key={`lbl-${dx}`} className="hm-row-lbl">{DLABS[di]}</div>
                    {AXES.map(ax => {
                      const p = grp.length ? pct(count(grp, d => d[`signal_${ax}`] === 'signalled'), grp.length) : 0
                      const col = hmColor(p, C.teal)
                      return (
                        <div key={`${dx}-${ax}`} className="hm-cell" style={{ background: col.bg, color: col.text }} title={`${DLABS[di]} × ${ax}: ${p}%`}>
                          {p}%
                        </div>
                      )
                    })}
                  </>
                )
              })}
            </div>
          </div>
          <p className="method" style={{ marginTop: 12 }}>Read across rows: "Of women with PCOS, X% have Androgen signal raised." High values confirm clinical validity of FEMM signal thresholds.</p>
        </div>
      </div>
    </section>
  )
}
